import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/react';
import posthog from 'posthog-js';

import { bootstrapGuestSession } from './services/guestSession';
import { labelAnalyticsTraffic } from './services/analyticsTraffic';
import { optionalTelemetryAllowed } from './services/appEnvironment';

// Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/base.css';
import './styles/design-system.css';
import './styles/layout.css';
import './styles/chat.css';
import './styles/bubbles.css';
import './styles/sidebar.css';
import './styles/travel-theme.css';

// --- Production-safe observability initialization ---
if (optionalTelemetryAllowed() && process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
  });
}

if (optionalTelemetryAllowed() && process.env.REACT_APP_POSTHOG_API_KEY) {
  posthog.init(process.env.REACT_APP_POSTHOG_API_KEY, {
    api_host: 'https://us.i.posthog.com',
    ui_host: 'https://us.posthog.com',
    // Keep the launch funnel intentional and privacy-bounded. SPA navigation
    // remains visible while arbitrary clicks, form values, and replay data do
    // not leave the browser.
    capture_pageview: 'history_change',
    autocapture: false,
    disable_session_recording: true,
    persistence: 'localStorage',
    before_send: labelAnalyticsTraffic,
  });
}

// Fire opportunistically — does not block render
bootstrapGuestSession().catch(() => {
  // Best effort. Auth hydration and the first planner request retry safely.
});

// Render app
const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Toaster
      position="top-center"
      toastOptions={{
        duration: 3000,
        style: {
          background: '#333',
          color: '#fff',
        },
      }}
    />
    <App />
  </React.StrictMode>
);
